/**
 * React Query hooks for Organizations
 * Provides automatic caching, refetching, and cache invalidation
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as organizationsService from "../services/organizations.service";
import type { OrganizationPaginationParams } from "../services/organizations.service";
import type { Organization } from "../types/organization.types";
import type { MapFilters } from "../services/organizations.service";
import type { UUID, PaginationParams } from "../types/common.types";

/**
 * Query key factory for organizations
 * Ensures consistent cache keys across the application
 */
export const organizationKeys = {
  all: ["organizations"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  list: (params: PaginationParams) =>
    [...organizationKeys.lists(), params] as const,
  details: () => [...organizationKeys.all, "detail"] as const,
  detail: (id: UUID) => [...organizationKeys.details(), id] as const,
  stats: (id: UUID) => [...organizationKeys.detail(id), "stats"] as const,
  map: (filters: MapFilters) =>
    [...organizationKeys.all, "map", filters] as const,
  mapZip: (lat: number, lng: number, radius: number, filters: MapFilters) =>
    [...organizationKeys.all, "mapZip", lat, lng, radius, filters] as const,
  mapLocations: (orgIds?: string[]) =>
    [...organizationKeys.all, "mapLocations", orgIds] as const,
};

/**
 * Hook to fetch paginated organizations list
 * Automatically refetches on window focus and manages cache
 */
export function useOrganizations(params: OrganizationPaginationParams) {
  console.log("[useOrganizations] Hook called with params:", params);

  return useQuery({
    queryKey: organizationKeys.list(params),
    queryFn: () => {
      console.log("[useOrganizations] queryFn executing...");
      return organizationsService.getOrganizations(params);
    },
    onSuccess: (data: any) => {
      console.log(
        "[useOrganizations] Query successful. Total:",
        data.total,
        "Data length:",
        data.data?.length,
      );
    },
    onError: (error: any) => {
      console.error("[useOrganizations] Query error:", error);
    },
  });
}

/**
 * Hook to fetch a single organization by ID
 */
export function useOrganization(id: UUID) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationsService.getOrganization(id),
    enabled: !!id, // Only run query if id is provided
  });
}

/**
 * Hook to fetch organization statistics
 */
export function useOrganizationStats(id: UUID) {
  return useQuery({
    queryKey: organizationKeys.stats(id),
    queryFn: () => organizationsService.getOrganizationStats(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new organization
 * Automatically invalidates the organizations list cache after success
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      organization: Omit<Organization, "id" | "created_at" | "updated_at">,
    ) => organizationsService.createOrganization(organization),
    onSuccess: () => {
      // Invalidate all organizations lists to trigger refetch
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing organization
 * Automatically invalidates related caches after success
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: UUID;
      updates: Partial<Organization>;
    }) => organizationsService.updateOrganization(id, updates),
    onSuccess: (data) => {
      // Invalidate the specific organization detail
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(data.id),
      });
      // Invalidate all organizations lists
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook to disable an organization
 * Automatically invalidates all organization caches after success
 */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => organizationsService.deleteOrganization(id),
    onSuccess: () => {
      // Invalidate all organizations data to trigger refetch
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

// =====================================================
// MAP VIEW HOOKS
// =====================================================

/**
 * Hook to fetch all organizations with coordinates for map display
 */
export function useOrganizationsForMap(filters: MapFilters) {
  return useQuery({
    queryKey: organizationKeys.map(filters),
    queryFn: () => organizationsService.getOrganizationsForMap(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - org locations don't change often
  });
}

/**
 * Hook to search organizations by zip code + radius
 */
export function useOrganizationsByZip(
  zipLat: number | null,
  zipLng: number | null,
  radiusMiles: number,
  filters: MapFilters,
) {
  return useQuery({
    queryKey: organizationKeys.mapZip(
      zipLat || 0,
      zipLng || 0,
      radiusMiles,
      filters,
    ),
    queryFn: () =>
      organizationsService.searchOrganizationsByZip(
        zipLat!,
        zipLng!,
        radiusMiles,
        filters,
      ),
    enabled: zipLat !== null && zipLng !== null,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch locations with coordinates for map display
 */
export function useLocationsForMap(orgIds?: string[], enabled: boolean = true) {
  return useQuery({
    queryKey: organizationKeys.mapLocations(orgIds),
    queryFn: () => organizationsService.getLocationsForMap(orgIds),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
